import "leaflet/dist/leaflet.css";
import React, { useRef } from "react";
import { BiXCircle } from "react-icons/bi";
import { GeoJSON, LayersControl, MapContainer, TileLayer, useMap } from "react-leaflet";
import { ZoneOverlays } from "../../../util/zoneOverlays";
import { mapDivisions } from "../../../constants";

function MapResizeHandler() {
  const map = useMap();
  React.useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 200);
    return () => clearTimeout(timer);
  }, [map]);
  return null;
}

const addFleetVesselTypeOptions = [
  {
    label: "Mini Bulk 0-20000",
    value: "0_20000",
  },
  {
    label: "HandyBulk 20,000-40,000",
    value: "20001_40000",
  },
  {
    label: "Supra/Ultra 40,000-68,000",
    value: "40001_68000",
  },
  {
    label: "P/Pmx 68,000-100,000",
    value: "68001_100000",
  },
  {
    label: "Cape 100,000 & above",
    value: "100001_99999999",
  },
  {
    label: "Unknown",
    value: "-",
  },
];

const tileLayers = {
  worldStreet: {
    name: "World Street Map",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}",
    attribution: "Esri",
  },
  seaMark: {
    name: "Sea Mark",
    url: "https://t1.openseamap.org/seamark/{z}/{x}/{y}.png",
    attribution: "OpenSeaMap",
  },
  osm: {
    name: "OpenStreetMap",
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    attribution: "OpenStreetMap contributors",
  },
  worldImagery: {
    name: "World Imagery",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    attribution: "Esri",
  },
  worldGrayCanvas: {
    name: "World Gray Canvas",
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    attribution: "Esri",
  },
  blackAndWhite: {
    name: "Black and White",
    url: "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png",
    attribution: "OpenStreetMap contributors",
  },
};
const AisMap = ({
  setSelectedVesselType,
  isLoading = false,
  selectedVesselType,
  onSubmit = () => { },
  selectedRegions,
  setSelectedRegions = () => { },
}) => {
  const layersRef = useRef({});
  const toggleRegionSelection = (feature, layer) => {
    const regionId = feature.properties.info;
    const isSelected = selectedRegions.some((r) => r.id === regionId);

    if (isSelected) {
      setSelectedRegions((prev) => prev.filter((r) => r.id !== regionId));
      layer.setStyle({ color: "#be1242", weight: 1, fillOpacity: 0.1 });
    } else {
      setSelectedRegions((prev) => [
        ...prev,
        {
          id: regionId,
          coordinates: { ...feature.geometry.coordinates[0] },
        },
      ]);
      layer.setStyle({
        color: "#2563eb",
        weight: 2,
        fillOpacity: 0.2,
        fillColor: "#2563eb",
      });
    }
  };

  const onEachFeature = (feature, layer) => {
    const regionId = feature.properties.info;
    layersRef.current[regionId] = layer;

    // Bind a popup with the region name
    if (feature.properties && feature.properties.info) {
      layer.bindPopup(feature.properties.info);
    }

    // Add hover effect to show the region name
    layer.on({
      mouseover: () => {
        layer
          .bindTooltip(feature.properties.info, {
            permanent: false,
            direction: "top",
          })
          .openTooltip();
      },
      mouseout: () => {
        layer.closeTooltip();
      },
      click: () => toggleRegionSelection(feature, layer),
    });
  };

  const handleRemoveRegion = (regionId) => {
    setSelectedRegions((prev) => prev.filter((r) => r.id !== regionId));
    const layer = layersRef.current[regionId];
    if (layer) {
      layer.setStyle({ color: "#be1242", weight: 1, fillOpacity: 0.1 });
    }
  };

  const handleAddToTable = () => {
    onSubmit(selectedRegions);
  };

  const handleVesselType = (e) => {
    setSelectedVesselType(e.target.value);
  };

  return (
    <div className="flex flex-col md:flex-row w-full md:h-[calc(100vh-230px)] md:min-h-[550px]">
      {/* Map Container */}
      <div className="h-[360px] md:h-full md:flex-1">
        <MapContainer
          center={[20, 0]}
          zoom={2}
          style={{ height: "100%", width: "100%" }}
        >
          <MapResizeHandler />
          <LayersControl position="topright">
            <LayersControl.BaseLayer name={tileLayers.worldImagery.name}>
              <TileLayer
                attribution={tileLayers.worldImagery.attribution}
                url={tileLayers.worldImagery.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer checked name={tileLayers.worldStreet.name}>
              <TileLayer
                attribution={tileLayers.worldStreet.attribution}
                url={tileLayers.worldStreet.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name={tileLayers.osm.name}>
              <TileLayer
                attribution={tileLayers.osm.attribution}
                url={tileLayers.osm.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name={tileLayers.worldGrayCanvas.name}>
              <TileLayer
                attribution={tileLayers.worldGrayCanvas.attribution}
                url={tileLayers.worldGrayCanvas.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name={tileLayers.blackAndWhite.name}>
              <TileLayer
                attribution={tileLayers.blackAndWhite.attribution}
                url={tileLayers.blackAndWhite.url}
              />
            </LayersControl.BaseLayer>

            <LayersControl.Overlay checked name="Region Boundaries">
              <GeoJSON
                key={JSON.stringify(selectedRegions)}
                data={mapDivisions}
                onEachFeature={onEachFeature}
                style={(feature) => {
                  const isSelected = selectedRegions.some(
                    (r) => r.id === feature.properties.info
                  );
                  return isSelected
                    ? {
                      color: "#be1242",
                      weight: 2,
                      fillOpacity: 0.5,
                      fillColor: "#be1242",
                    }
                    : { color: "#2563eb", weight: 1, fillOpacity: 0.2 };
                }}
              />
            </LayersControl.Overlay>
          <ZoneOverlays />
          </LayersControl>
        </MapContainer>
      </div>

      {/* Sidebar */}
      <div className="w-full md:w-64 bg-white md:h-full min-h-[160px] flex flex-col px-2 md:pl-3 md:pr-2 py-1">
        {/* Header & Vessel Type Filter */}
        <div className="pb-2 flex flex-col gap-1.5">
          <div className="flex items-center">
            <div className="w-1 h-3.5 bg-[#313C4C] rounded-none mr-2"></div>
            <h2 className="text-[13px] font-semibold text-[#1e293b]">
              Selected Regions
            </h2>
          </div>
          <div>
            <label
              htmlFor="vessel-type"
              className="block text-[11px] font-medium text-slate-600 mb-1"
            >
              Vessel Type
            </label>
            <select
              id="vessel-type"
              className="w-full h-[26px] px-2 text-[11px] border border-[#cbd5e1] rounded-none outline-none bg-white text-[#1e293b] font-medium leading-tight"
              onChange={handleVesselType}
              value={selectedVesselType}
            >
              <option value="all">All</option>
              {addFleetVesselTypeOptions?.map((val, ind) => {
                return (
                  <option
                    key={ind}
                    value={val.label === "unknown" ? "unknown" : val.value}
                  >
                    {val.label}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Content Area with scroll */}
        <div
          className="flex-1 overflow-y-auto py-1 max-h-[200px] md:max-h-none"
          style={{ scrollbarWidth: "thin" }}
        >
          {selectedRegions.length > 0 ? (
            <div className="space-y-1.5">
              {selectedRegions.map((region) => (
                <div
                  key={region.id}
                  className="flex items-center text-[11px] justify-between p-1.5 bg-white border border-slate-200 rounded-none hover:border-[#313C4C] transition-all group"
                >
                  <span className="truncate text-slate-700 font-medium">
                    {region.id}
                  </span>
                  <button
                    onClick={() => handleRemoveRegion(region.id)}
                    className="text-slate-400 hover:text-red-500 transition-colors p-0.5"
                    aria-label={`Remove ${region.id}`}
                  >
                    <BiXCircle className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center p-3">
              <p className="text-slate-500 font-medium text-[11px] mb-1">No regions selected</p>
              <p className="text-slate-400 text-[10px]">
                Select regions from the map to add them here
              </p>
            </div>
          )}
        </div>

        {/* Footer with Apply Button */}
        {selectedRegions.length > 0 && (
          <div className="pt-2 pb-1 bg-white sticky bottom-0 border-t border-slate-100 md:border-t-0">
            <button
              onClick={handleAddToTable}
              disabled={isLoading}
              className={`w-full h-[26px] px-3 ${isLoading ? "bg-slate-400 border-slate-400" : "bg-[#313C4C] hover:bg-[#0c2f50] border-[#313C4C]"
                } text-white font-medium text-[11px] rounded-none border cursor-pointer transition-colors flex items-center justify-center`}
            >
              <span>{isLoading ? "Applying changes..." : `Apply Changes (${selectedRegions.length})`}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AisMap;
